import { call, put } from 'redux-saga/effects';
import { getPathWithQueryString, pageContext, nestFlatQuery } from 'ctm-util-functions';
import { runApiOnMountForPage } from './run_api_on_mount_for_page';
import { runApiOnNextForPage } from './run_api_on_next_for_page';

const JOURNEY_STEPS_NAMES = {
    START: 'start',
    REGO_STATE: 'car_rego_state',
    CAR_MAKE: 'car_make',
    COVER_TYPE: 'cover_type'
};
const JOURNEY_NAMES = { CAR: 'car-insurance', HOME_CONTENT: 'home_content-insurance' };

/**
 * Generator handle customize
 * @param {Object} query - a object maps URL query keys with their values
 * @param {Object} pageState - Tightly coupled to PageWrapper mapStateToProps(), it has all page settings.
 * @param {Object} journeyModel - journeyModel instance so we can get first page schema
 * @param {string} searchStr - The complete, unparsed query string - eg '?rego=1CC2CC'
 */
export const handleCustomizeUrlQueries = function* (query, pageState, journeyModel, searchStr) {
    const journeyName = extractJourneyName(pageState.path);
    if (journeyName === JOURNEY_NAMES.CAR) {
        // handle car-journey specific logic about URL queries
        yield call(prefillCarJourneyData, query);
        yield call(handleRedirectInCarJourney, query, journeyModel, pageState, searchStr);
    } else if (journeyName === JOURNEY_NAMES.HOME_CONTENT) {
        // TODO handle home-content-journey specific logic about URL queries later
    }
};

/**
 * Helper function abstract journey name from its path
 * @param {string} pagePath - the Journey URL path
 * @returns {string} - the journey name
 */
const extractJourneyName = (pagePath) => {
    return pagePath.split('/')[1];
};

/**
 * Generator for set query values to Redux store
 * @param {Object} query - a object maps URL query keys with their values
 */
const prefillCarJourneyData = function* (query) {
    const { rego, state } = query;
    yield put({
        type: 'SET_VALUES',
        values: nestFlatQuery({
            'coverDetail.vehicle.state': state,
            'coverDetail.vehicle.rego': rego
        }),
        stateKey: 'vertical.quote'
    });
};

/**
 * redirect to different pages in car journey:
 * if URL path contains 'manual_enter' with a true value, redirect to car_make page.
 * if URL path contains both "rego" and "state", redirect to cover_type page.
 * if URL path contains either "rego" or "state", redirect to rego_state page.
 * @param {Object} query - a object maps URL query keys with their values
 * @param {Object} journeyModel - journeyModel instance so we can get first page schema
 * @param {Object} pageState - Tightly coupled to PageWrapper mapStateToProps(), it has all page settings.
 * @param {Object} searchStr - The complete, unparsed query string - eg '?rego=1CC2CC'
 * */
const handleRedirectInCarJourney = function* (query, journeyModel, pageState, searchStr) {
    const { isManuallyEnteringCarInfo, rego, state } = query;
    const firstPage = journeyModel.getFirstStepPage();

    // call onMount api of the start page to make sure new journey has started
    yield call(runApiOnMountForPage, firstPage, pageState);

    if (isManuallyEnteringCarInfo) {
        yield call(redirectToStep,
            pageState,
            journeyModel,
            searchStr,
            JOURNEY_STEPS_NAMES.CAR_MAKE);
    } else if (rego && state) {
        yield call(redirectToStep,
            pageState,
            journeyModel,
            location.search,
            JOURNEY_STEPS_NAMES.COVER_TYPE);
    } else if (rego && !state) {
        yield call(redirectToStep,
            pageState,
            journeyModel,
            searchStr,
            JOURNEY_STEPS_NAMES.REGO_STATE);
    } else if ((!rego && state)) {
        yield call(redirectToStep,
            pageState,
            journeyModel,
            searchStr,
            JOURNEY_STEPS_NAMES.START);
    }
};

/**
 * Generator to redirect to journey start and run the onMount api on that page, NO SSR
 * @param {Object} pageState - Tightly coupled to PageWrapper mapStateToProps(), it has all page settings.
 * @param {Object} journeyModel - journeyModel instance so we can get first page schema
 * @param {string} searchStr - The complete, unparsed query string - eg '?rego=1CC2CC'
 * @param {string} targetPage - the index of the target step
 * @private
 */
const redirectToStep = function* (pageState, journeyModel, searchStr, targetPage) {
    const targetStepPage = journeyModel.getPageByName(targetPage);
    const browserHistory = pageContext.getProps().browserHistory;
    const querylessPath = getPathWithQueryString(targetStepPage.path).split('?')[0];

    // handle the special case that redirect to the cover_type page of car journey
    if (targetPage === JOURNEY_STEPS_NAMES.COVER_TYPE) {
        const regoStatePage = journeyModel.getPageByName(JOURNEY_STEPS_NAMES.REGO_STATE);

        // redirect to step page with trimmed URL, because query logic has already been handled
        yield call(browserHistory.replace, querylessPath);
        // manually perform a car lookup, api set as onNext of rego-state page.
        yield call(runApiOnNextForPage, regoStatePage, pageState);
    } else {
        // redirect to step page with trimmed URL, because query logic has already been handled
        yield call(browserHistory.replace, querylessPath);
    }
};
